import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Bell, Sparkles, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAllActiveBriefJobs, useJobWatcher, type ActiveBriefJob, type BriefRetryPayload } from "@/lib/jobWatcher";
import { useListWorkspaces } from "@workspace/api-client-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface NotificationCopy {
  en: string;
  es: string;
}

interface NotificationItem {
  id: number;
  type: string;
  workspaceId: number | null;
  title: NotificationCopy;
  body: NotificationCopy;
  linkPath: string | null;
  createdAt: string;
  readAt: string | null;
  metadata: Record<string, unknown> | null;
}

function extractRetryPayload(meta: Record<string, unknown> | null): BriefRetryPayload | null {
  if (!meta || typeof meta !== "object") return null;
  const raw = (meta as { retryPayload?: unknown }).retryPayload;
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.title !== "string" || typeof r.useBookmarked !== "boolean") return null;
  return {
    title: r.title,
    useBookmarked: r.useBookmarked,
    collectionId:
      typeof r.collectionId === "number" ? r.collectionId : undefined,
    itemIds: Array.isArray(r.itemIds)
      ? r.itemIds.filter((n): n is number => typeof n === "number")
      : undefined,
  };
}

interface ListResponse {
  items: NotificationItem[];
  nextCursor: number | null;
}

interface CountResponse {
  count: number;
}

export const NOTIF_UNREAD_QK = ["notifications", "unread-count"] as const;
const NOTIF_LIST_QK = ["notifications", "list"] as const;

async function fetchUnreadCount(): Promise<CountResponse> {
  const res = await fetch(`${BASE}/api/notifications/unread-count`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("count failed");
  return res.json();
}

async function fetchList(): Promise<ListResponse> {
  const res = await fetch(`${BASE}/api/notifications?limit=20`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("list failed");
  return res.json();
}

async function postMarkAllRead(): Promise<void> {
  const res = await fetch(`${BASE}/api/notifications/read-all`, {
    method: "POST",
    credentials: "include",
    headers: { "sec-fetch-site": "same-origin" },
  });
  if (!res.ok) throw new Error("mark-all failed");
}

async function postMarkRead(id: number): Promise<void> {
  const res = await fetch(`${BASE}/api/notifications/${id}/read`, {
    method: "PATCH",
    credentials: "include",
    headers: { "sec-fetch-site": "same-origin" },
  });
  if (!res.ok) throw new Error("mark failed");
}

function formatRelative(iso: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return t("justNow");
  if (mins < 60) return t("minutesAgo", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("hoursAgo", { count: hours });
  const days = Math.floor(hours / 24);
  return t("daysAgo", { count: days });
}

// Backend stores `linkPath` like `/workspaces/12/results/3` — locale-agnostic.
// On the frontend, the wouter Router is mounted at `/<base>` or `/<base>/es`
// depending on the active locale. Strip a leading `/` and let wouter prepend
// the active locale's base so navigating preserves `/es/...` for Spanish users.
function navTarget(linkPath: string): string {
  return linkPath.startsWith("/") ? linkPath : `/${linkPath}`;
}

interface PanelProps {
  items: NotificationItem[];
  isLoading: boolean;
  isError: boolean;
  count: number;
  lang: "en" | "es";
  t: (k: string, o?: Record<string, unknown>) => string;
  tApp: (k: string, o?: Record<string, unknown>) => string;
  onMarkAll: () => void;
  markAllPending: boolean;
  onItemClick: (item: NotificationItem) => void;
  activeBriefJobs: ActiveBriefJob[];
  onRetryFromNotification: (item: NotificationItem) => void;
  workspaceNameById: (id: number | null | undefined) => string | undefined;
}

function NotificationPanel({
  items,
  isLoading,
  isError,
  count,
  lang,
  t,
  tApp,
  onMarkAll,
  markAllPending,
  onItemClick,
  activeBriefJobs,
  onRetryFromNotification,
  workspaceNameById,
}: PanelProps) {
  return (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <p className="text-sm font-semibold text-white/90">{t("title")}</p>
        {count > 0 && (
          <button
            onClick={onMarkAll}
            disabled={markAllPending}
            className="text-[11px] font-medium text-vl-orange hover:text-white transition-colors disabled:opacity-50"
          >
            {t("markAllRead")}
          </button>
        )}
      </div>
      <div className="max-h-[60vh] sm:max-h-[400px] overflow-y-auto">
        {activeBriefJobs.length > 0 && (
          <div className="border-b border-white/[0.06] bg-vl-orange/[0.04]">
            <p className="px-4 pt-3 pb-1.5 text-[10px] uppercase tracking-wider font-semibold text-white/45">
              {tApp("briefs.inProgressTitle")}
            </p>
            <ul>
              {activeBriefJobs.map((job) => {
                const wsName = job.workspaceName ?? workspaceNameById(job.workspaceId);
                return (
                  <li
                    key={job.jobId}
                    className="flex items-center gap-3 px-4 py-2.5"
                    data-testid={`bell-brief-progress-${job.jobId}`}
                  >
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-vl-orange/15 border border-vl-orange/30 flex items-center justify-center">
                      <Sparkles className="w-3.5 h-3.5 text-vl-orange animate-pulse" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-white/95 truncate leading-tight">
                        {job.title}
                      </p>
                      {wsName && (
                        <p className="text-[11px] text-white/45 truncate mt-0.5">
                          {wsName}
                        </p>
                      )}
                    </div>
                    <div
                      className="flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 border-vl-orange/40 border-t-vl-orange animate-spin"
                      aria-hidden
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {isLoading && (
          <p className="px-4 py-6 text-xs text-white/40 text-center">{t("loading")}</p>
        )}
        {isError && (
          <p className="px-4 py-6 text-xs text-red-400/80 text-center">{t("errorLoading")}</p>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-white/70">{t("empty")}</p>
            <p className="text-xs text-white/40 mt-1.5">{t("emptyHint")}</p>
          </div>
        )}
        {!isLoading && !isError && items.length > 0 && (
          <ul className="divide-y divide-white/[0.04]">
            {items.map((item) => {
              const unread = !item.readAt;
              const canRetry =
                item.type === "brief.failed" && extractRetryPayload(item.metadata) !== null;
              return (
                <li key={item.id} className={cn(unread && "bg-vl-orange/[0.04]")}>
                  <div className="flex gap-3 px-4 py-3 transition-colors hover:bg-white/[0.03]">
                    <button
                      onClick={() => onItemClick(item)}
                      className="flex gap-3 flex-1 min-w-0 text-left"
                    >
                      <span
                        className={cn(
                          "mt-1.5 w-1.5 h-1.5 rounded-full shrink-0",
                          unread ? "bg-vl-orange" : "bg-transparent",
                        )}
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <p className={cn(
                          "text-[13px] leading-snug",
                          unread ? "font-semibold text-white/95" : "text-white/70",
                        )}>
                          {item.title?.[lang] ?? item.title?.en ?? ""}
                        </p>
                        <p className="text-xs text-white/55 mt-0.5 line-clamp-2">
                          {item.body?.[lang] ?? item.body?.en ?? ""}
                        </p>
                        <p className="text-[10px] text-white/30 mt-1">
                          {formatRelative(item.createdAt, t)}
                        </p>
                      </div>
                    </button>
                    {canRetry && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRetryFromNotification(item);
                        }}
                        className="flex-shrink-0 self-start mt-0.5 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg transition-opacity hover:opacity-90 active:opacity-75"
                        style={{
                          background: "rgba(239,68,68,0.12)",
                          border: "1px solid rgba(239,68,68,0.35)",
                          color: "#ef4444",
                        }}
                        data-testid={`bell-brief-retry-${item.id}`}
                        aria-label={tApp("briefs.retryButton")}
                      >
                        <RotateCw className="w-3 h-3" />
                        {tApp("briefs.retryButton")}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

export function NotificationBell({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) {
  const { t, i18n } = useTranslation("notifications");
  const { t: tApp } = useTranslation("app");
  const lang: "en" | "es" = i18n.language?.startsWith("es") ? "es" : "en";
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const activeBriefJobs = useAllActiveBriefJobs();
  const { retryBrief } = useJobWatcher();
  const { data: workspaces } = useListWorkspaces({
    query: { queryKey: ["/api/workspaces"], staleTime: 60_000 },
  });
  const workspaceNameById = (id: number | null | undefined): string | undefined => {
    if (id == null) return undefined;
    const found = (workspaces as Array<{ id: number; name: string }> | undefined)?.find(
      (w) => w.id === id,
    );
    return found?.name;
  };
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { data: countData } = useQuery({
    queryKey: NOTIF_UNREAD_QK,
    queryFn: fetchUnreadCount,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const { data: listData, isLoading, isError } = useQuery({
    queryKey: NOTIF_LIST_QK,
    queryFn: fetchList,
    enabled: open,
    staleTime: 5_000,
  });

  const markAllMut = useMutation({
    mutationFn: postMarkAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIF_UNREAD_QK });
      queryClient.invalidateQueries({ queryKey: NOTIF_LIST_QK });
    },
  });

  const markOneMut = useMutation({
    mutationFn: postMarkRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIF_UNREAD_QK });
      queryClient.invalidateQueries({ queryKey: NOTIF_LIST_QK });
    },
  });

  // Desktop popover only — measure on open (and on resize) so the panel
  // never extends past the right edge of the viewport. With `left: 0` as
  // the default anchor, we shift the panel left by however many pixels it
  // would otherwise overflow. (It can't overflow on the left since the
  // bell itself is in the left sidebar.)
  useEffect(() => {
    if (variant !== "desktop" || !open) return;
    function reposition() {
      const panel = popoverRef.current;
      const btn = buttonRef.current;
      if (!panel || !btn) return;
      const btnRect = btn.getBoundingClientRect();
      const panelWidth = panel.offsetWidth || 340;
      const margin = 12;
      const overflowRight = btnRect.left + panelWidth + margin - window.innerWidth;
      if (overflowRight > 0) {
        setPanelStyle({ left: `-${overflowRight}px` });
      } else {
        setPanelStyle({});
      }
    }
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [open, variant]);

  // Desktop popover only — click-outside + Escape close.
  useEffect(() => {
    if (variant !== "desktop" || !open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node | null;
      if (
        target &&
        !popoverRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, variant]);

  const count = countData?.count ?? 0;
  const items = listData?.items ?? [];
  // Bell badge reflects unread notifications PLUS any brief jobs the
  // user has in flight, so they always have a persistent visual cue
  // that work is happening — even after navigating away from /briefs.
  const badgeCount = count + activeBriefJobs.length;

  function handleRetryFromNotification(item: NotificationItem) {
    const payload = extractRetryPayload(item.metadata);
    if (!payload || item.workspaceId == null) return;
    if (!item.readAt) markOneMut.mutate(item.id);
    void retryBrief({
      workspaceId: item.workspaceId,
      payload,
      workspaceName: workspaceNameById(item.workspaceId),
    });
  }

  function handleItemClick(item: NotificationItem) {
    if (!item.readAt) markOneMut.mutate(item.id);
    setOpen(false);
    if (item.linkPath) {
      // wouter's Router was mounted with a locale-aware base in App.tsx
      // (`/...` for English, `/es/...` for Spanish). Passing a relative-to-
      // base path here preserves the locale prefix automatically.
      setLocation(navTarget(item.linkPath));
    }
  }

  const trigger = (
    <button
      ref={buttonRef}
      onClick={() => setOpen((v) => !v)}
      title={t("open")}
      aria-label={t("open")}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-testid="notification-bell-trigger"
      className={cn(
        "relative rounded-lg transition-all",
        variant === "desktop"
          ? "p-1.5 text-white/30 hover:text-white/70 hover:bg-white/[0.04]"
          : "p-2 text-white/60 hover:text-white hover:bg-white/10",
      )}
    >
      <Bell className={variant === "desktop" ? "w-3.5 h-3.5" : "w-5 h-5"} />
      {badgeCount > 0 && (
        <span
          data-testid="notification-bell-badge"
          className={cn(
            "absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full text-white text-[9px] font-bold leading-none",
            activeBriefJobs.length > 0 ? "bg-vl-orange" : "bg-vl-deep",
          )}
          aria-label={t("unreadBadge", { count: badgeCount })}
        >
          {badgeCount > 9 ? "9+" : badgeCount}
        </span>
      )}
    </button>
  );

  // Mobile: full-width bottom sheet via Radix Dialog.
  if (variant === "mobile") {
    return (
      <div className="relative">
        {trigger}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="bottom"
            className="bg-[#0d0d12] border-t border-white/10 p-0 max-h-[85vh] overflow-hidden"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{t("title")}</SheetTitle>
            </SheetHeader>
            <NotificationPanel
              items={items}
              isLoading={isLoading}
              isError={isError}
              count={count}
              lang={lang}
              t={t}
              tApp={tApp}
              onMarkAll={() => markAllMut.mutate()}
              markAllPending={markAllMut.isPending}
              onItemClick={handleItemClick}
              activeBriefJobs={activeBriefJobs}
              onRetryFromNotification={handleRetryFromNotification}
              workspaceNameById={workspaceNameById}
            />
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Desktop: anchored popover. The bell lives in the LEFT sidebar, so we
  // anchor to the bell's left edge (panel grows to the right, into the main
  // content area) instead of its right edge (which would push the panel off
  // the left side of the viewport).
  return (
    <div className="relative">
      {trigger}
      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={t("title")}
          className={cn(
            "absolute z-50 w-[340px] max-w-[calc(100vw-24px)] rounded-xl shadow-2xl",
            "bg-[#0d0d12] border border-white/10",
            "bottom-full mb-2 left-0",
          )}
          style={panelStyle}
        >
          <NotificationPanel
            items={items}
            isLoading={isLoading}
            isError={isError}
            count={count}
            lang={lang}
            t={t}
            tApp={tApp}
            onMarkAll={() => markAllMut.mutate()}
            markAllPending={markAllMut.isPending}
            onItemClick={handleItemClick}
            activeBriefJobs={activeBriefJobs}
            onRetryFromNotification={handleRetryFromNotification}
            workspaceNameById={workspaceNameById}
          />
        </div>
      )}
    </div>
  );
}
